import { Storage, API, Auth } from 'aws-amplify';

import {
  REPORT_YEAR,
  EMPLOYEE_REPORT_TYPE,
  CONTRACTOR_REPORT_TYPE,
  DATA_UPLOAD_METHOD
} from 'constants/common';
import { getAllRows, getFirstRow } from 'utils/graphql';
import {
  reportsBySlug as ReportsBySlug,
  listReports as ListReports,
  getReport as GetReport
} from 'graphql/queries';
import { Report, GetReportQuery, CreateReportMutation, UpdateReportMutation } from 'graphql-api';
import { GraphQLResult } from '@aws-amplify/api';
import { createReport as CreateReport, updateReport as UpdateReport } from 'graphql/mutations';
import { authHeader } from 'utils/auth';
import { ReportFilingDataInterface } from 'types/report';

export async function getReports(variables: Object = {}): Promise<Report[]> {
  return await getAllRows(ListReports, 'listReports', variables);
}

export async function getReport(reportId: string): Promise<Report | null> {
  const responseData = (await API.graphql({
    query: GetReport,
    variables: { id: reportId },
    authMode: 'AMAZON_COGNITO_USER_POOLS'
  })) as GraphQLResult<GetReportQuery>;
  return responseData.data?.getReport ?? null;
}

export async function getReportBySlug(reportSlug: string): Promise<Report | null | undefined> {
  return await getFirstRow(ReportsBySlug, 'reportsBySlug', { slug: reportSlug });
}

export async function getContractorReport(): Promise<Report | null | undefined> {
  return await getFirstRow(ListReports, 'listReports', {
    filter: { year: { eq: REPORT_YEAR }, reportType: { eq: CONTRACTOR_REPORT_TYPE } }
  });
}

export async function updateReport(
  report: Report | null | undefined,
  data: Object
): Promise<Report | null> {
  if (report == null) {
    return null;
  }

  const responseData = (await API.graphql({
    query: UpdateReport,
    variables: { input: { id: report.id, ...data } },
    authMode: 'AMAZON_COGNITO_USER_POOLS'
  })) as GraphQLResult<UpdateReportMutation>;
  return responseData.data?.updateReport ?? null;
}

export function reportName(reportType: string, year: number): string {
  return `${year} ${
    reportType === CONTRACTOR_REPORT_TYPE ? 'Labor Contractor Report' : 'Payroll Employee Report'
  }`;
}

export function reportResultFileName(report: Report): string {
  return reportName(report.reportType, report.year).toLowerCase().replaceAll(' ', '_');
}

export function reportValidatedFileName(report: Report): string {
  return `${report.year}_${
    report.reportType === CONTRACTOR_REPORT_TYPE ? 'contractor' : 'employee'
  }_report_source_data.csv`;
}

export function reportHRISInputFileName(report: Report): string {
  return `${report.year}_${
    report.reportType === CONTRACTOR_REPORT_TYPE ? 'contractor' : 'employee'
  }_report_hris_data.csv`;
}

async function createReport(reportType: string, attrs: Object = {}): Promise<Report | null> {
  try {
    const reportAttrs = { year: REPORT_YEAR, reportType, ...attrs };
    const slug = reportName(reportAttrs.reportType, reportAttrs.year)
      .toLowerCase()
      .replaceAll(' ', '-');
    const responseData = (await API.graphql({
      query: CreateReport,
      variables: { input: { ...reportAttrs, slug } },
      authMode: 'AMAZON_COGNITO_USER_POOLS'
    })) as GraphQLResult<CreateReportMutation>;
    return responseData.data?.createReport ?? null;
  } catch (error) {
    console.log('Error: ', error);
    return null;
  }
}

export async function createEmployeeReport(attrs: Object = {}): Promise<Report | null> {
  return await createReport(EMPLOYEE_REPORT_TYPE, attrs);
}

export async function createContractorReport(attrs: Object = {}): Promise<Report | null> {
  return await createReport(CONTRACTOR_REPORT_TYPE, {
    dataImportMethod: DATA_UPLOAD_METHOD,
    ...attrs
  });
}

export async function uploadFile(
  report: Report | null,
  file: File,
  fileName: string
): Promise<string | null> {
  if (report == null) {
    return null;
  }

  const { type: mimeType } = file;
  const cognitoIdentityId = (await Auth.currentUserCredentials()).identityId;
  const fileKey = `report/report-${report?.id ?? ''}/${fileName}`;
  await Storage.put(fileKey, file, {
    contentType: mimeType,
    level: 'private'
  });
  return `private/${cognitoIdentityId}/${fileKey}`;
}

export async function getFileBlob(filePath: string | null | undefined): Promise<Blob | null> {
  if (filePath === null || filePath === undefined) {
    return null;
  } else {
    const cognitoIdentityId = (await Auth.currentUserCredentials()).identityId;
    const fileKey = filePath.replace(`private/${cognitoIdentityId}/`, '');
    return (
      await Storage.get(fileKey, {
        download: true,
        level: 'private'
      })
    ).Body as Blob;
  }
}

export async function getInputFileBlob(report: Report): Promise<Blob | null> {
  return await getFileBlob(report.inputFile);
}

export async function getValidatedFileBlob(report: Report): Promise<Blob | null> {
  return await getFileBlob(report.validatedFile);
}

export async function generateReport(reportId: string): Promise<Report | null> {
  const path = `/report/${reportId}/result/generate`;
  const apiName = 'reportResultAPI';
  const headers = await authHeader();
  try {
    return await API.get(apiName, path, {
      headers
    });
  } catch (error) {
    console.log('Error: ', error);
    return null;
  }
}

export async function uploadInputFile(report: Report, inputFile: File): Promise<Report | null> {
  const uploadedFileKey: string | null = await uploadFile(report, inputFile, inputFile.name);
  if (uploadedFileKey != null) {
    return await updateReport(report, {
      inputFile: uploadedFileKey,
      oneschemaSessionToken: null,
      oneschemaSessionTokenExpiredTime: null,
      oneschemaData: null
    });
  } else {
    return null;
  }
}

export async function getReportPreview(
  reportId: string
): Promise<Array<Record<string, string>> | null> {
  const path = `/report/${reportId}/result/preview`;
  const apiName = 'reportResultAPI';
  const headers = await authHeader();
  try {
    return await API.get(apiName, path, {
      headers
    });
  } catch (error) {
    console.log('Error: ', error);
    return null;
  }
}

export async function getReportResultCSV(reportId: string): Promise<File | null> {
  const path = `/report/${reportId}/result/csv`;
  const apiName = 'reportResultAPI';
  const headers = await authHeader();
  try {
    return await API.get(apiName, path, {
      responseType: 'blob',
      headers
    });
  } catch (error) {
    console.log('Error: ', error);
    return null;
  }
}

export async function getReportResult(
  reportId: string
): Promise<Array<Record<string, string>> | null> {
  const path = `/report/${reportId}/result`;
  const apiName = 'reportResultAPI';
  const headers = await authHeader();
  try {
    return await API.get(apiName, path, {
      headers
    });
  } catch (error) {
    console.log('Error: ', error);
    return null;
  }
}

export async function getFilingData(reportId: string): Promise<ReportFilingDataInterface | null> {
  const path = `/report/${reportId}/result/filing-data`;
  const apiName = 'reportResultAPI';
  const headers = await authHeader();
  try {
    return await API.get(apiName, path, {
      headers
    });
  } catch (error) {
    console.log('Error: ', error);
    return null;
  }
}
